// ============================================================================
// FSA EDIT CLASSIFICATION MODAL  (FSA-2.1.8)
// ============================================================================
// Opens from the Trial Balance row action menu. Lets the user pick a
// classification (Schedule -> Class) and apply it to:
//   • this ledger only (single-entry write — reuses existing bulkClassify
//                       with one entry id), OR
//   • all ledgers in the entry's parent_group (new group-classify endpoint
//                       with a replace policy: skip-mapped / replace-non-manual
//                       / replace-all).
//
// The Schedule and leaf Class options come from the engagement's template
// hierarchy via useFsaTemplateHierarchy. Selecting a Schedule node (no
// leaf) saves as schedule-only; selecting a Class node saves as
// fully-classified.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutTreeSelect, type TreeSelectOption } from 'components/ui';
import notificationService from 'services/notificationService';
import { fsaClassificationService } from 'api/services/fsa/fsaClassificationService';
import { useFsaTemplateHierarchy } from 'hooks/api/fsa/useFsaTemplates';
import { useFsaEngagementCustomClasses } from 'hooks/api/fsa/useFsaNotes';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import type { ID } from 'api/types';
import type { TrialBalanceEntry } from 'api/types/fsa/trialBalance.types';
import type {
  GroupClassifyPolicy,
  GroupClassifyImpact,
} from 'api/types/fsa/classification.types';

interface Props {
  isOpen: boolean;
  /** Consolidated TB id. */
  trialBalanceId: ID;
  /** The row the user opened the menu on. */
  entry: TrialBalanceEntry | null;
  /** Active template — drives the Classification picker options. */
  templateId: ID | null;
  /**
   * FSA-2.2 step 4b — engagement id. Drives the custom-classes lookup so
   * the picker can expose engagement-time custom classes alongside
   * template classes. Optional for backwards compatibility; when omitted
   * the picker only shows template options (custom classes won't appear).
   */
  engagementId?: ID | null;
  /**
   * Optional Note-scoped picker. When set (e.g. opened from inside the
   * Note Modal's Ledgers pane), the Classification picker shows ONLY the
   * matching schedule's classes — the auditor is already working within
   * a specific Note and shouldn't have to scroll the full template tree
   * to find the right leaf. When omitted (e.g. opened from the Trial
   * Balance tab), the full template tree shows as before.
   *
   * The "Show all notes" toggle inside the picker lets the auditor
   * escape the scope when they actually do want to reclassify across
   * notes — preserves the existing flow without burning a navigation
   * step in the common case.
   */
  scopeScheduleId?: ID | null;
  onClose: () => void;
  /** Fires after a successful save so the screen can refetch. */
  onSaved: () => void;
  // ── Minimize-to-bar support ──
  tabId?: string;
  moduleName?: string;
  /**
   * Re-opens the modal for a previously-minimized entry. The modal passes
   * back the FULL entry object (captured in its closure at minimize time)
   * so the parent doesn't have to look it up — that lookup was a stale
   * closure trap: by the time the user clicks Resume, the parent's
   * `rows` array may have paged away from the original page and the
   * lookup would fail.
   */
  onRestoreModal?: (entry: TrialBalanceEntry) => void;
  /** Parent exposes a ref to call handleMinimize programmatically (auto-minimize when the user opens another row's modal). */
  minimizeRef?: React.RefObject<(() => void) | null>;
}

type Scope = 'ledger' | 'group';

// Synthetic prefixes keep schedules + classes + custom classes in the
// same tree-select option pool. We unpack them at save time.
// FSA-2.2 step 4b — `custom:` prefix wraps a composite key
// `<scheduleId>|<localId>` so both halves survive the picker's flat
// string value contract.
const SCHEDULE_PREFIX = 'sched:';
const CLASS_PREFIX    = 'class:';
const CUSTOM_PREFIX   = 'custom:';

const FsaEditClassificationModal: React.FC<Props> = ({
  isOpen, trialBalanceId, entry, templateId, engagementId, scopeScheduleId, onClose, onSaved,
  tabId, moduleName, onRestoreModal, minimizeRef,
}) => {
  const { minimize } = useModalMinimize();

  const groupName = (entry?.parentGroup ?? '').trim();
  const hasGroup = groupName.length > 0;

  // ---------- Form state ----------
  const [scope, setScope] = useState<Scope>('ledger');
  const [pickedValue, setPickedValue] = useState<string>('');
  const [policy, setPolicy] = useState<GroupClassifyPolicy>('skip-mapped');
  // When opened with `scopeScheduleId`, default the tree to the
  // Note-scoped view. Toggle to false to reveal every Note's classes.
  // Falls back to "show all" when no scope was supplied (TB-tab usage).
  const [scopeToNote, setScopeToNote] = useState<boolean>(!!scopeScheduleId);
  // Reset the toggle when the scope prop changes (e.g. modal reopened
  // from a different Note's Ledgers pane).
  useEffect(() => {
    setScopeToNote(!!scopeScheduleId);
  }, [scopeScheduleId]);

  // ---------- Refs for stable minimize-time capture ----------
  // (closures inside `minimize({...})` need the CURRENT values; props/state
  // shift if the parent opens a different entry before restore fires.)
  const entryRef = useRef(entry);
  entryRef.current = entry;
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const pickedValueRef = useRef(pickedValue);
  pickedValueRef.current = pickedValue;
  const policyRef = useRef(policy);
  policyRef.current = policy;
  const initialSnapshotRef = useRef('');
  const isRestoreRef = useRef(false);

  // Reset whenever the modal opens with a (possibly different) entry.
  useEffect(() => {
    if (!isOpen) return;
    // Skip reset on restore — the minimize.onRestore handler has already
    // set scope / pickedValue / policy to the captured snapshot.
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      return;
    }
    setScope('ledger');
    setPolicy('skip-mapped');
    // Pre-select the entry's current mapping if any.
    let nextPicked = '';
    if (entry?.customClassLocalId && entry?.scheduleId) {
      // FSA-2.2 step 4b — custom-class target encodes both schedule + local id.
      nextPicked = `${CUSTOM_PREFIX}${entry.scheduleId}|${entry.customClassLocalId}`;
    } else if (entry?.scheduleClassId) {
      nextPicked = `${CLASS_PREFIX}${entry.scheduleClassId}`;
    } else if (entry?.scheduleId) {
      nextPicked = `${SCHEDULE_PREFIX}${entry.scheduleId}`;
    }
    setPickedValue(nextPicked);
    // Initial snapshot for dirty tracking on minimize.
    initialSnapshotRef.current = JSON.stringify({
      scope: 'ledger', picked: nextPicked, policy: 'skip-mapped',
    });
    setImpact(null);
    setImpactKey('');
  }, [isOpen, entry?.id, entry?.scheduleClassId, entry?.scheduleId]);

  // ---------- Tree-select options from template hierarchy ----------
  const { data: hierarchy, loading: hierarchyLoading } = useFsaTemplateHierarchy(templateId);

  // FSA-2.2 step 4b — engagement-time custom classes, grouped by schedule.
  // Lookup map drives the picker's "custom" leaves under each schedule.
  const { data: engagementCustomClasses } = useFsaEngagementCustomClasses(engagementId ?? null);

  const customByScheduleId = useMemo(() => {
    const map = new Map<string, { localId: string; name: string; displayOrder: number; ledgerCount: number }[]>();
    (engagementCustomClasses ?? []).forEach(c => {
      const key = String(c.scheduleId);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push({
        localId: c.localId,
        name: c.name,
        displayOrder: c.displayOrder,
        ledgerCount: c.ledgerCount,
      });
    });
    // Stable ascending displayOrder per schedule.
    map.forEach(list => list.sort((a, b) => a.displayOrder - b.displayOrder));
    return map;
  }, [engagementCustomClasses]);

  const treeOptions = useMemo<TreeSelectOption[]>(() => {
    if (!hierarchy?.reports) return [];
    const opts: TreeSelectOption[] = [];

    // Each schedule becomes a root (or grouped under its report code) and
    // its leaf classes become children. We keep it 2-level to stay
    // readable; multi-level class hierarchies (where a class has parent_class_id)
    // are flattened to top-level under the schedule with indent applied via
    // the `parentValue` chain so the connector lines still draw correctly.
    //
    // When the picker is Note-scoped (`scopeScheduleId` + `scopeToNote`),
    // skip every schedule that isn't the current Note's. The auditor
    // can flip `scopeToNote` off to see the full tree.
    const scopedScheduleStr = scopeScheduleId != null ? String(scopeScheduleId) : null;
    hierarchy.reports.forEach(report => {
      report.schedules?.forEach(node => {
        const s = node.schedule;
        if (!s?.id) return;
        if (scopeToNote && scopedScheduleStr && String(s.id) !== scopedScheduleStr) return;
        const scheduleVal = `${SCHEDULE_PREFIX}${s.id}`;
        opts.push({
          value: scheduleVal,
          label: `${report.report.code} · ${s.name}`,
          parentValue: null,
        });

        // Walk classes recursively, threading parentValue for tree shape.
        // MEMO_ONLY classes (e.g. Authorised Capital on Note 1) and
        // MANUAL_DISCLOSURE classes (Trade Receivables disputed/undisputed
        // × good/doubtful split, Trade Payables MSME/Others split, EPS
        // classes — Note 35 — share counts + per-share Rs values) are
        // disclosure-only: auditor types each amount on the Note's
        // Main view rather than mapping TB ledgers to them. Excluded
        // from the picker entirely (children skipped too — a non-mappable
        // class can't have mappable descendants by definition).
        const walk = (cls: any, parentVal: string) => {
          if (!cls?.id) return;
          const role = typeof cls.classRole === 'string' ? cls.classRole.toUpperCase() : '';
          if (role === 'MEMO_ONLY' || role === 'MANUAL_DISCLOSURE') return;
          const cv = `${CLASS_PREFIX}${cls.id}`;
          opts.push({
            value: cv,
            label: cls.name,
            parentValue: parentVal,
          });
          (cls.children ?? []).forEach((c: any) => walk(c, cv));
        };
        (node.classes ?? []).forEach((cls: any) => walk(cls, scheduleVal));

        // FSA-2.2 step 4b — engagement-time custom classes for this schedule
        // sit as siblings of the schedule's template classes. We tag the
        // label so the auditor can tell custom buckets apart at a glance.
        const customs = customByScheduleId.get(String(s.id)) ?? [];
        customs.forEach(c => {
          opts.push({
            value: `${CUSTOM_PREFIX}${s.id}|${c.localId}`,
            label: c.ledgerCount > 0
              ? `${c.name} (custom · ${c.ledgerCount})`
              : `${c.name} (custom)`,
            parentValue: scheduleVal,
          });
        });
      });
    });
    return opts;
  }, [hierarchy, customByScheduleId, scopeScheduleId, scopeToNote]);

  // ---------- Impact preview ----------
  const [impact, setImpact] = useState<GroupClassifyImpact | null>(null);
  const [impactKey, setImpactKey] = useState('');
  const [impactLoading, setImpactLoading] = useState(false);
  const impactReqIdRef = useRef(0);

  const refreshImpact = useCallback(async () => {
    if (scope !== 'group' || !hasGroup) return;
    const reqId = ++impactReqIdRef.current;
    setImpactLoading(true);
    try {
      const r = await fsaClassificationService.groupImpact(trialBalanceId, groupName, policy);
      if (reqId !== impactReqIdRef.current) return; // stale
      setImpact(r.data ?? null);
      setImpactKey(`${groupName}|${policy}`);
    } catch (err: any) {
      if (reqId !== impactReqIdRef.current) return;
      setImpact(null);
      notificationService.toast.error(err?.response?.data?.message || 'Could not compute group impact.');
    } finally {
      if (reqId === impactReqIdRef.current) setImpactLoading(false);
    }
  }, [scope, hasGroup, trialBalanceId, groupName, policy]);

  // Auto-refresh impact when scope/policy/group changes.
  useEffect(() => { refreshImpact(); }, [refreshImpact]);

  // ---------- Save ----------
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const parsedPick = useMemo(() => {
    if (pickedValue.startsWith(CUSTOM_PREFIX)) {
      // FSA-2.2 step 4b — composite value `<scheduleId>|<localId>`.
      const composite = pickedValue.substring(CUSTOM_PREFIX.length);
      const sep = composite.indexOf('|');
      if (sep > 0) {
        return {
          scheduleClassId: null as ID | null,
          scheduleId: composite.substring(0, sep) as ID,
          customClassLocalId: composite.substring(sep + 1),
        };
      }
    }
    if (pickedValue.startsWith(CLASS_PREFIX))
      return {
        scheduleClassId: pickedValue.substring(CLASS_PREFIX.length) as ID,
        scheduleId: null as ID | null,
        customClassLocalId: null as string | null,
      };
    if (pickedValue.startsWith(SCHEDULE_PREFIX))
      return {
        scheduleClassId: null as ID | null,
        scheduleId: pickedValue.substring(SCHEDULE_PREFIX.length) as ID,
        customClassLocalId: null as string | null,
      };
    return {
      scheduleClassId: null as ID | null,
      scheduleId: null as ID | null,
      customClassLocalId: null as string | null,
    };
  }, [pickedValue]);

  const handleSave = useCallback(() => {
    if (!pickedValue) {
      notificationService.toast.warning('Pick a Schedule or Class first.');
      return;
    }
    setSaveConfirmOpen(true);
  }, [pickedValue]);

  const handleConfirmSave = useCallback(async () => {
    if (!entry) return;
    setSaveConfirmOpen(false);
    setIsSaving(true);
    try {
      if (scope === 'ledger') {
        if (parsedPick.customClassLocalId && parsedPick.scheduleId) {
          // FSA-2.2 step 4b — engagement-time custom class write.
          await fsaClassificationService.bulkClassify(trialBalanceId, {
            entryIds: [entry.id],
            customClassLocalId: parsedPick.customClassLocalId,
            scheduleId: parsedPick.scheduleId,
          });
        } else if (parsedPick.scheduleClassId) {
          await fsaClassificationService.bulkClassify(trialBalanceId, {
            entryIds: [entry.id],
            scheduleClassId: parsedPick.scheduleClassId,
          });
        } else if (parsedPick.scheduleId) {
          await fsaClassificationService.bulkScheduleMap(trialBalanceId, {
            entryIds: [entry.id],
            scheduleId: parsedPick.scheduleId,
          });
        }
        notificationService.toast.success(`'${entry.ledgerName}' updated.`);
      } else {
        // Group scope accepts every target shape the single-ledger path
        // does — template leaf, engagement custom class (scheduleId carries
        // the owning Note), or schedule-only.
        const r = await fsaClassificationService.groupClassify(trialBalanceId, {
          groupName,
          scheduleClassId: parsedPick.scheduleClassId ?? undefined,
          scheduleId: parsedPick.scheduleId ?? undefined,
          customClassLocalId: parsedPick.customClassLocalId ?? undefined,
          policy,
        });
        const res = r.data;
        notificationService.toast.success(
          res
            ? `${res.updated} ledger${res.updated === 1 ? '' : 's'} updated · ${res.skipped} skipped (policy: ${res.policy})`
            : 'Group classification applied.'
        );
      }
      onSaved();
      onClose();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Save failed.');
    } finally {
      setIsSaving(false);
    }
  }, [entry, scope, parsedPick, trialBalanceId, groupName, policy, onSaved, onClose]);

  // ---------- Minimize-to-bar handler ----------
  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const cEntry  = entryRef.current;
    if (!cEntry) return;
    const cScope  = scopeRef.current;
    const cPicked = pickedValueRef.current;
    const cPolicy = policyRef.current;
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot
      ? JSON.stringify({ scope: cScope, picked: cPicked, policy: cPolicy }) !== savedSnapshot
      : false;

    const modalId = `${tabId}-fsa-edit-cls-${cEntry.id}`;
    const labelLedger = cEntry.ledgerName.length > 32
      ? cEntry.ledgerName.substring(0, 32) + '…'
      : cEntry.ledgerName;
    const title = `Edit Classification — ${labelLedger}`;

    minimize({
      id: modalId,
      title,
      moduleName,
      tabId,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        // Restore this session's form state in the closure BEFORE the parent
        // re-opens the modal (otherwise the open-effect won't see them).
        setScope(cScope);
        setPickedValue(cPicked);
        setPolicy(cPolicy);
        initialSnapshotRef.current = savedSnapshot;
        if (onRestoreModal) onRestoreModal(cEntry);
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  // Expose handleMinimize so the parent can auto-minimize this modal when
  // the user opens a different row's edit modal.
  if (minimizeRef) minimizeRef.current = handleMinimize;

  if (!isOpen || !entry) return null;

  return (
    <>
      <div className="modal-backdrop" onClick={isSaving ? undefined : onClose} />
      {/* No `position: relative` — `.modal-container` is already `position: fixed`
          (top/left 50% + translate-half-self centers it). An inline `relative`
          override kicks the modal back into the document flow and pushes it to
          the bottom. The LoadingOverlay's `position: absolute` works fine
          against a fixed parent. */}
      <div className="modal-container modal-lg">
        {isSaving && <LoadingOverlay />}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Edit Classification</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button
                className="modal-header-btn"
                onClick={handleMinimize}
                title="Minimize to bar"
                disabled={isSaving}
              >
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={onClose} disabled={isSaving}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content">
          {/* Ledger / Group readouts */}
          <div style={readoutRowStyle}>
            <Readout label="Ledger" value={entry.ledgerName} />
            <Readout label="Group"  value={hasGroup ? groupName : '—'} />
          </div>

          {/* Scope picker */}
          <div className="form-group" style={{ marginTop: 8 }}>
            <label className="form-label">Scope</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <ScopeChip
                active={scope === 'ledger'}
                onClick={() => setScope('ledger')}
                label="This ledger only"
              />
              <ScopeChip
                active={scope === 'group'}
                disabled={!hasGroup}
                onClick={() => hasGroup && setScope('group')}
                label={hasGroup ? `All ledgers in group "${groupName}"` : 'Group scope unavailable (no group on this row)'}
              />
            </div>
          </div>

          {/* Tree picker */}
          <div className="form-group" style={{ marginTop: 12 }}>
            <SyutTreeSelect
              label={
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                  Classification <span className="text-danger">*</span>
                  {scopeScheduleId != null && (
                    <button
                      type="button"
                      onClick={() => setScopeToNote((v) => !v)}
                      style={scopeToggleStyle(scopeToNote)}
                      title={
                        scopeToNote
                          ? "Showing this Note's classes only. Click to see every Note."
                          : 'Showing every Note. Click to scope back to this Note.'
                      }
                    >
                      {scopeToNote ? 'Scoped to this Note' : 'Showing all Notes'}
                    </button>
                  )}
                </span>
              }
              placeholder={hierarchyLoading ? 'Loading template…' : 'Pick a Schedule or Class'}
              options={treeOptions}
              value={pickedValue || null}
              onChange={(v) => setPickedValue((v as string) || '')}
              isClearable
              size="md"
              showChildCount
              showConnectorLines
              showNodeIcons
              defaultExpandedValues={
                // Pre-expand the current Note's schedule whenever it's
                // known — either via the scopeScheduleId prop (Note Modal
                // → Ledgers pane flow) or via the entry's current scheduleId
                // (TB-tab edit flow). Saves the auditor a click in both cases.
                scopeScheduleId != null
                  ? [`${SCHEDULE_PREFIX}${scopeScheduleId}`]
                  : entry.scheduleId
                    ? [`${SCHEDULE_PREFIX}${entry.scheduleId}`]
                    : []
              }
            />
            <div style={hintStyle}>
              Pick a <strong>Class</strong> for a full classification, a <strong>custom class</strong> declared on the Note, or a <strong>Schedule</strong> (Note) to leave the leaf class for later. Any of these can be applied to this ledger only or to the whole group.
            </div>
          </div>

          {/* Replace policy — group mode only */}
          {scope === 'group' && (
            <div className="form-group" style={{ marginTop: 12 }}>
              <label className="form-label">Replace policy</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <PolicyRadio
                  policy="skip-mapped"
                  selected={policy}
                  onSelect={setPolicy}
                  label="Skip already-mapped"
                  hint="Only fills ledgers that have no mapping. Safest. Manual + auto-classified rows are preserved."
                />
                <PolicyRadio
                  policy="replace-non-manual"
                  selected={policy}
                  onSelect={setPolicy}
                  label="Replace auto + unmapped (preserve manual)"
                  hint="Overwrites unmapped + schedule-only + auto-classified. Manually classified rows are kept as-is."
                />
                <PolicyRadio
                  policy="replace-all"
                  selected={policy}
                  onSelect={setPolicy}
                  label="Replace everything"
                  hint="Overwrites all rows in this group (control accounts are still skipped)."
                />
              </div>
            </div>
          )}

          {/* Impact preview — group mode */}
          {scope === 'group' && hasGroup && (
            <div style={impactBlockStyle}>
              {impactLoading && impactKey === '' ? (
                <div style={{ color: 'var(--text-secondary)' }}>Computing impact…</div>
              ) : impact ? (
                <>
                  <div style={impactHeaderStyle}>
                    Impact under <strong>'{impact.policy}'</strong>
                  </div>
                  <div style={impactStatRowStyle}>
                    <ImpactStat label="Will update" value={impact.willUpdate} highlight />
                    <ImpactStat label="Will skip"   value={impact.willSkip} />
                    <ImpactStat label="Total"        value={impact.totalInGroup} muted />
                  </div>
                  <div style={impactBreakdownStyle}>
                    Breakdown:
                    <span style={chipStyle('#f1f5f9', '#475569')}>Unmapped {impact.unmapped}</span>
                    <span style={chipStyle('#fef3c7', '#92400e')}>Schedule-only {impact.scheduleOnly}</span>
                    <span style={chipStyle('#dbeafe', '#1e40af')}>Auto {impact.auto}</span>
                    <span style={chipStyle('#dcfce7', '#166534')}>Manual {impact.manual}</span>
                    {impact.controlAccounts > 0 && (
                      <span style={chipStyle('#f3e8ff', '#6b21a8')}>Control {impact.controlAccounts}</span>
                    )}
                  </div>
                </>
              ) : null}
            </div>
          )}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={isSaving}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={isSaving || !pickedValue || (scope === 'group' && impact?.willUpdate === 0)}
            title={scope === 'group' && impact?.willUpdate === 0 ? 'No rows match the chosen policy — nothing to update.' : undefined}
          >
            {isSaving
              ? 'Saving…'
              : scope === 'ledger'
                ? 'Save'
                : `Apply to ${impact?.willUpdate ?? '…'} ledger${impact?.willUpdate === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title="Confirm Save"
        message={
          scope === 'ledger'
            ? `Apply classification to '${entry.ledgerName}'?`
            : `Apply classification to ${impact?.willUpdate ?? '…'} ledger(s) in group '${groupName}' (policy: ${policy})? ${impact?.willSkip ?? 0} will be skipped.`
        }
        confirmLabel="Apply"
        cancelLabel="Cancel"
        variant={scope === 'group' && policy === 'replace-all' ? 'danger' : 'default'}
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />
    </>
  );
};

// ============================================================================
// Subcomponents
// ============================================================================

const Readout: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div style={{ flex: 1, minWidth: 0 }}>
    <div style={readoutLabelStyle}>{label}</div>
    <div style={readoutValueStyle} title={value}>{value}</div>
  </div>
);

const ScopeChip: React.FC<{ active: boolean; disabled?: boolean; onClick: () => void; label: string }> = ({
  active, disabled, onClick, label,
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    style={{
      padding: '6px 10px',
      borderRadius: 999,
      fontSize: '0.8rem',
      fontWeight: 600,
      border: '1px solid var(--form-border-color)',
      background: active ? '#2563eb' : 'var(--bg-primary)',
      color: active ? '#fff' : (disabled ? 'var(--text-muted)' : 'var(--text-secondary)'),
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.55 : 1,
    }}
  >
    {label}
  </button>
);

const PolicyRadio: React.FC<{
  policy: GroupClassifyPolicy;
  selected: GroupClassifyPolicy;
  onSelect: (p: GroupClassifyPolicy) => void;
  label: string;
  hint: string;
}> = ({ policy, selected, onSelect, label, hint }) => (
  <label style={{
    display: 'flex', alignItems: 'flex-start', gap: 8,
    padding: '6px 8px', borderRadius: 6,
    background: selected === policy ? 'rgba(37,99,235,0.07)' : 'transparent',
    cursor: 'pointer',
  }}>
    <input
      type="radio"
      name="group-policy"
      checked={selected === policy}
      onChange={() => onSelect(policy)}
      style={{ marginTop: 3 }}
    />
    <div>
      <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{hint}</div>
    </div>
  </label>
);

const ImpactStat: React.FC<{ label: string; value: number; highlight?: boolean; muted?: boolean }> = ({
  label, value, highlight, muted,
}) => (
  <div style={{ flex: 1, minWidth: 0 }}>
    <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
      {label}
    </div>
    <div style={{
      fontSize: '1.05rem',
      fontWeight: 700,
      color: muted ? 'var(--text-secondary)' : highlight ? '#16a34a' : 'var(--text-primary)',
    }}>
      {value.toLocaleString('en-IN')}
    </div>
  </div>
);

const LoadingOverlay: React.FC = () => (
  <div style={{
    position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
  }}>
    <div style={{
      width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
      borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
    }} />
    <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Saving…</span>
  </div>
);

// ============================================================================
// Styles
// ============================================================================

const readoutRowStyle: React.CSSProperties = {
  display: 'flex', gap: 16, padding: 10,
  background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: 6,
};
const readoutLabelStyle: React.CSSProperties = {
  fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--text-secondary)',
};
const readoutValueStyle: React.CSSProperties = {
  fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)',
  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
};
const scopeToggleStyle = (active: boolean): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '2px 8px',
  borderRadius: 999,
  fontSize: '0.62rem',
  fontWeight: 700,
  letterSpacing: 0.3,
  textTransform: 'uppercase',
  cursor: 'pointer',
  border: active ? '1px solid #1d4ed8' : '1px solid #cbd5e1',
  background: active ? '#dbeafe' : '#f1f5f9',
  color: active ? '#1d4ed8' : '#475569',
});
const hintStyle: React.CSSProperties = {
  fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 4,
};
const impactBlockStyle: React.CSSProperties = {
  marginTop: 14, padding: 10,
  background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: 6,
};
const impactHeaderStyle: React.CSSProperties = {
  fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: 6,
};
const impactStatRowStyle: React.CSSProperties = {
  display: 'flex', gap: 12,
};
const impactBreakdownStyle: React.CSSProperties = {
  marginTop: 8, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6,
  fontSize: '0.75rem', color: 'var(--text-secondary)',
};
const chipStyle = (bg: string, color: string): React.CSSProperties => ({
  display: 'inline-block', padding: '2px 8px', borderRadius: 999,
  background: bg, color, fontSize: '0.7rem', fontWeight: 600,
});

export default FsaEditClassificationModal;
